import { diffLines } from 'diff';
import type { MutableRefObject } from 'react';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { StreamingDiffParser } from '~/lib/runtime/diff-parser';
import { diffRecorder } from '~/lib/runtime/diff-recorder';
import type { PartialFileCallback } from '~/lib/runtime/message-parser';
import { workbenchStore } from '~/lib/stores/workbench';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { invariant } from '~/utils/invariant';
import { isLikelyToBeLazy } from '~/utils/laziness';
import { createScopedLogger } from '~/utils/logger';
import { addWorkDir } from '~/utils/workDir';

const logger = createScopedLogger('useMessages/on-file-stream');

interface Context {
  diffParserRef: MutableRefObject<StreamingDiffParser | undefined>;
}

/**
 * Function invovked by the `StreamingMessageParser` when a file content
 * is being received.
 */
export function onFileStream({ action, data }: Parameters<PartialFileCallback>[0], { diffParserRef }: Context) {
  const isEnd = data.status === 'end';

  if (action.actionType === 'Update' && action.contentType === 'diff') {
    let diffParser = diffParserRef.current;

    if (!diffParser) {
      const absolutePath = addWorkDir(action.filePath);
      const currentFile = workbenchStore.files.get()[absolutePath];

      invariant(currentFile?.type === 'file', 'Expected file');

      diffRecorder.add(action.filePath, currentFile.content);

      diffParser = new StreamingDiffParser(currentFile.content, {
        invalidHunk: handleInvalidHunk,
      });

      diffParserRef.current = diffParser;
    }

    const diff = data.content;

    diffRecorder.record(action.filePath, diff);

    const parsedContent = diffParser.parse(diff, isEnd);

    if (isEnd) {
      diffParserRef.current = undefined;

      const originalFileContentSize = diffParser.originalFileContent.length;
      const diffSize = diff.length;
      const sizeDifference = originalFileContentSize - diffSize;

      if (import.meta.env.DEV) {
        logger.info('Diff size difference', sizeDifference);
      }

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.DiffFinishedStreaming,
          properties: {
            originalFileContentSize,
            diffSize,
            sizeDifference,
          },
        },
      });

      const { unappliedChanges } = diffParser.finalize();

      if (unappliedChanges.length > 0) {
        if (import.meta.env.DEV) {
          logger.warn(`Failed to apply all changes to ${data.path}`);
          console.groupCollapsed('Info');
          console.info(unappliedChanges);
          console.info(diffParser.originalFileContent);
          console.info(data.content);
          console.groupEnd();
        }

        workbenchStore.setSelectedFile(data.path);

        const absoluteFilePath = addWorkDir(data.path);

        /**
         * We're using a map to track files that are being fixed in multiple concurrent
         * async operations.
         *
         * So every time we fix a diff we add an entry in the map and remove it
         * when it's done.
         */
        workbenchStore.pendingFixes.setKey(absoluteFilePath, true);

        action.fixDiff(parsedContent, diffParser.originalFileContent, diff, (content, done) => {
          // when fixing the diff has completed, check laziness
          if (done) {
            handleLaziness({ action, data }, content);

            return;
          }

          workbenchStore.setStreamingFile({
            ...data,
            status: 'stream',
            content,
          });
        });

        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.FixingFile,
            properties: {
              filePath: data.path,
              originalFileContent: diffParser.originalFileContent,
              diff: data.content,
              fixing: 'diff',
            },
          },
        });

        // we fall back to the original file content
        return diffParser.originalFileContent;
      }
    }

    if (isEnd && import.meta.env.DEV) {
      logger.info(`Applied ${action.contentType}`);
    }

    // handle laziness when the file finished streaming
    if (isEnd) {
      return handleLaziness({ action, data }, parsedContent);
    }

    workbenchStore.setStreamingFile({
      ...data,
      content: parsedContent,
    });

    return parsedContent;
  }

  const newFileContent = data.content;

  // handle laziness after an update in non-diff mode
  if (isEnd && action.actionType === 'Update') {
    return handleLaziness({ action, data }, newFileContent);
  }

  workbenchStore.setStreamingFile(data);

  return undefined;
}

/**
 * Check for laziness and if necessary start a fixLaziness action.
 *
 * @param fileCallbackParams Same parameters provided to `onFileStream`.
 * @param newFileContent The new file content (possibly post-processed).
 */
function handleLaziness({ action, data }: Parameters<PartialFileCallback>[0], newFileContent: string) {
  const absoluteFilePath = addWorkDir(action.filePath);

  const earlyReturnCleanup = () => {
    // remove any pending fixes on this file
    workbenchStore.pendingFixes.setKey(absoluteFilePath, undefined);

    workbenchStore.setStreamingFile({
      ...data,
      content: newFileContent,
    });

    return newFileContent;
  };

  // if no laziness is detected we return the new file content as is
  if (!isLikelyToBeLazy(newFileContent)) {
    return earlyReturnCleanup();
  }

  const files = workbenchStore.files.get();
  const currentFile = files[absoluteFilePath];

  invariant(currentFile?.type === 'file');

  const originalFileContent = currentFile.content;

  /**
   * We also check if we detect laziness in a diff to make sure that the laziness
   * was introduced by the AI model. We only look at additions as we care about new
   * lines added by the model.
   */
  const hasLaziness = diffLines(originalFileContent, newFileContent).some(
    ({ added, value }) => added && isLikelyToBeLazy(value),
  );

  if (!hasLaziness) {
    return earlyReturnCleanup();
  }

  /**
   * In the case where the current file was already lazy and we don't see lazyness added by the model,
   * we don't try to fix it and return.
   */
  if (isLikelyToBeLazy(originalFileContent)) {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.LazinessPresent,
      },
    });

    return earlyReturnCleanup();
  }

  workbenchStore.setSelectedFile(data.path);

  workbenchStore.pendingFixes.setKey(absoluteFilePath, true);

  action.fixLaziness(newFileContent, originalFileContent, (content, done) => {
    if (done) {
      action.content.set(content);

      workbenchStore.pendingFixes.setKey(absoluteFilePath, undefined);

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.FixingFile,
          properties: {
            filePath: data.path,
            originalFileContentSize: originalFileContent.length,
            stillLazy: isLikelyToBeLazy(content),
            fixing: 'laziness',
          },
        },
      });
    }

    workbenchStore.setStreamingFile({
      ...data,
      status: done ? 'end' : 'stream',
      content,
    });
  });

  // we fall back to the original file content
  return originalFileContent;
}

function handleInvalidHunk(line: string) {
  logger.warn('Invalid hunk', line);

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.InvalidHunk,
      properties: {
        line,
      },
    },
  });
}
